import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Product } from '@hygh-vybe/lib';
import { CATALOG_URL } from '../config';

const PAGE_SIZE = 60;

export interface ProductFilters {
  query: string;
  brand: string | null;
  format: string | null;
  potencyMin: number | null;
  potencyMax: number | null;
  effect: string | null;
  buyable?: boolean;
}

export interface UseProductsResult {
  products: Product[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  totalCount: number;
  loadMore: () => void;
  suggestions: string[];
  hasFetched: boolean;
}

// ─── Catalog Cache (shared across hook instances) ───────────────────────────

interface CatalogEntry {
  productId: string;
  brandName: string;
  productName: string;
  thcContentMg: number;
  cbdContentMg?: number;
  beverageFormat: string;
  volumeOz: number;
  flavorProfile?: string;
  priceUsd?: number;
  imageUrl?: string;
  aggregateScore: number;
  totalRatings: number;
  buyable: boolean;
  sponsored: boolean;
  buyUrl?: string;
  createdAt: string;
}

let catalogCache: CatalogEntry[] | null = null;
let catalogPromise: Promise<CatalogEntry[]> | null = null;

async function loadCatalog(): Promise<CatalogEntry[]> {
  if (catalogCache) return catalogCache;

  if (!catalogPromise) {
    catalogPromise = fetch(CATALOG_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load catalog (${res.status})`);
        return res.json();
      })
      .then((json) => {
        catalogCache = json.products as CatalogEntry[];
        return catalogCache;
      })
      .catch((err) => {
        catalogPromise = null; // Allow retry on failure
        throw err;
      });
  }

  return catalogPromise;
}

// ─── Search & Filter (client-side) ─────────────────────────────────────────

function matchesFilters(product: CatalogEntry, filters: ProductFilters): boolean {
  const { query, brand, format, potencyMin, potencyMax, buyable } = filters;

  if (buyable && !product.buyable) return false;

  if (brand) {
    if (product.brandName.toLowerCase() !== brand.toLowerCase()) return false;
  }

  if (format) {
    if (product.beverageFormat.toLowerCase() !== format.toLowerCase()) return false;
  }

  if (potencyMin != null && product.thcContentMg < potencyMin) return false;
  if (potencyMax != null && product.thcContentMg > potencyMax) return false;

  if (query) {
    const q = query.toLowerCase();
    const nameMatch = product.productName.toLowerCase().includes(q);
    const brandMatch = product.brandName.toLowerCase().includes(q);
    const flavorMatch = (product.flavorProfile || '').toLowerCase().includes(q);
    const formatMatch = product.beverageFormat.toLowerCase().includes(q);
    if (!nameMatch && !brandMatch && !flavorMatch && !formatMatch) return false;
  }

  return true;
}

function relevanceScore(product: CatalogEntry, query: string): number {
  const q = query.toLowerCase();
  const name = product.productName.toLowerCase();
  const brand = product.brandName.toLowerCase();

  if (name === q || brand === q) return 100;
  if (name.startsWith(q) || brand.startsWith(q)) return 75;
  if (name.includes(q) || brand.includes(q)) return 50;
  if ((product.flavorProfile || '').toLowerCase().includes(q)) return 25;
  if (product.beverageFormat.toLowerCase().includes(q)) return 10;
  return 0;
}

// ─── Shuffle (seeded per session for stable scroll) ─────────────────────────

const SESSION_SEED = Math.random();

function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

function shuffleArray<T>(arr: T[]): T[] {
  const shuffled = [...arr];
  const rand = seededRandom(SESSION_SEED * 2147483647);
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const temp = shuffled[i]!;
    shuffled[i] = shuffled[j]!;
    shuffled[j] = temp;
  }
  return shuffled;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useProducts(filters: ProductFilters): UseProductsResult {
  const [catalog, setCatalog] = useState<CatalogEntry[] | null>(catalogCache);
  const [loading, setLoading] = useState(!catalogCache);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(!!catalogCache);
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);
  const prevFiltersRef = useRef<string>('');

  // Load catalog on mount (instant if already cached)
  useEffect(() => {
    if (catalogCache) {
      setCatalog(catalogCache);
      setLoading(false);
      setHasFetched(true);
      return;
    }

    let cancelled = false;
    setLoading(true);

    loadCatalog()
      .then((data) => {
        if (!cancelled) {
          setCatalog(data);
          setLoading(false);
          setHasFetched(true);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load products');
          setLoading(false);
          setHasFetched(true);
        }
      });

    return () => { cancelled = true; };
  }, []);

  // Reset pagination when filters change
  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    if (prevFiltersRef.current !== filtersKey) {
      prevFiltersRef.current = filtersKey;
      setDisplayCount(PAGE_SIZE);
    }
  }, [filtersKey]);

  // Filter and sort (memoized — instant on every keystroke)
  const filtered = useMemo(() => {
    if (!catalog) return [];

    let results = catalog.filter((p) => matchesFilters(p, filters));

    // Sort by relevance if there's a text query
    if (filters.query) {
      const q = filters.query;
      results.sort((a, b) => {
        const relA = relevanceScore(a, q);
        const relB = relevanceScore(b, q);
        if (relA !== relB) return relB - relA;
        return b.aggregateScore - a.aggregateScore;
      });
    } else if (filters.brand) {
      // Brand filter active — sort alphabetically by product name
      results.sort((a, b) => a.productName.localeCompare(b.productName));
    } else if (!filters.format && filters.potencyMin == null && filters.potencyMax == null && !filters.buyable) {
      // No filters active — shuffle for discovery (seeded per session so scroll stays stable)
      results = shuffleArray(results);
    }
    // When other filters are active but no text query or brand, keep default score order from catalog

    return results;
  }, [catalog, filters]);

  const totalCount = filtered.length;
  const products = useMemo(() => filtered.slice(0, displayCount) as unknown as Product[], [filtered, displayCount]);
  const hasMore = displayCount < totalCount;

  const loadMore = useCallback(() => {
    setDisplayCount((prev) => prev + PAGE_SIZE);
  }, []);

  return {
    products,
    loading,
    loadingMore: false,
    error,
    hasMore,
    totalCount,
    loadMore,
    suggestions: [],
    hasFetched,
  };
}
